import React, { useState, useEffect } from 'react';
import { FaTimes, FaExternalLinkAlt, FaShoppingCart, FaGift } from 'react-icons/fa';

/**
 * AdPopup - Modern popup ad component with smart timing and user-friendly design
 */
const AdPopup = ({ 
  ad, 
  isVisible, 
  onClose, 
  onAction,
  showAfter = 3000, // Show after 3 seconds
  showOnlyOnce = true,
  cookieName = 'quickcrate_express_popup_shown'
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [animation, setAnimation] = useState('');

  useEffect(() => {
    if (!isVisible || !ad) return;

    // Check if popup was already shown (if showOnlyOnce is true)
    if (showOnlyOnce) {
      const wasShown = localStorage.getItem(cookieName);
      if (wasShown) {
        return;
      }
    }

    // Show after specified delay
    const timer = setTimeout(() => {
      setShouldShow(true);
      setAnimation('animate-fadeInUp');
      
      // Mark as shown
      if (showOnlyOnce) {
        localStorage.setItem(cookieName, Date.now().toString());
      }
    }, showAfter);

    return () => clearTimeout(timer);
  }, [isVisible, ad, showAfter, showOnlyOnce, cookieName]);

  const handleClose = () => {
    setAnimation('animate-fadeOutDown');
    setTimeout(() => {
      setShouldShow(false);
      onClose?.();
    }, 300);
  };

  const handleAction = (actionType) => {
    if (onAction) {
      onAction(actionType, ad);
    }
    
    if (ad.link && actionType === 'cta') {
      if (ad.openInNewTab) {
        window.open(ad.link, '_blank');
      } else {
        window.location.href = ad.link;
      }
    }
    
    handleClose();
  };

  if (!shouldShow || !ad) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden ${animation}`}
        style={{ animationDuration: '0.3s' }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 bg-black bg-opacity-20 hover:bg-opacity-40 text-white p-2 rounded-full transition-all duration-200"
        >
          <FaTimes className="w-4 h-4" />
        </button>

        {/* Header Image/Video */}
        {ad.imageUrl && (
          <div className="relative h-48 bg-gradient-to-br from-blue-500 to-purple-600">
            {ad.type === 'video' ? (
              <video
                className="w-full h-full object-cover"
                src={ad.videoUrl}
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                src={ad.imageUrl}
                alt={ad.title}
                className="w-full h-full object-cover"
              />
            )}
            
            {/* Badge */}
            {ad.badge && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                {ad.badge}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Title */}
          {ad.title && (
            <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
              {ad.title}
            </h3>
          )}

          {/* Description */}
          {ad.description && (
            <p className="text-gray-600 mb-4 leading-relaxed">
              {ad.description}
            </p>
          )}

          {/* Features/Benefits */}
          {ad.features && ad.features.length > 0 && (
            <ul className="mb-4 space-y-2">
              {ad.features.map((feature, index) => (
                <li key={index} className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3 flex-shrink-0"></div>
                  {feature}
                </li>
              ))}
            </ul>
          )}

          {/* Price/Offer */}
          {ad.offer && (
            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Special Offer:</span>
                <span className="text-lg font-bold text-orange-600">{ad.offer}</span>
              </div>
              {ad.originalPrice && (
                <div className="text-sm text-gray-500">
                  Was: <span className="line-through">{ad.originalPrice}</span>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Primary CTA */}
            {ad.ctaText && (
              <button
                onClick={() => handleAction('cta')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2"
              >
                {ad.ctaIcon === 'cart' && <FaShoppingCart className="w-4 h-4" />}
                {ad.ctaIcon === 'gift' && <FaGift className="w-4 h-4" />}
                {ad.ctaIcon === 'external' && <FaExternalLinkAlt className="w-4 h-4" />}
                {ad.ctaText}
              </button>
            )}

            {/* Secondary CTA */}
            {ad.secondaryCta && (
              <button
                onClick={() => handleAction('secondary')}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-6 rounded-lg transition-all duration-200"
              >
                {ad.secondaryCta}
              </button>
            )}
          </div>

          {/* Footer */}
          {ad.footer && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                {ad.footer}
              </p>
            </div>
          )}

          {/* Timer (for limited time offers) */}
          {ad.countdown && (
            <div className="mt-4 text-center">
              <div className="text-xs text-gray-500 mb-1">Offer expires in:</div>
              <CountdownTimer targetDate={ad.countdown} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Countdown Timer Component
 */
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setTimeLeft({});
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex justify-center space-x-2 text-sm font-mono">
      {Object.keys(timeLeft).length > 0 ? (
        <>
          {timeLeft.days > 0 && (
            <span className="bg-red-500 text-white px-2 py-1 rounded">
              {timeLeft.days}d
            </span>
          )}
          <span className="bg-red-500 text-white px-2 py-1 rounded">
            {String(timeLeft.hours).padStart(2, '0')}h
          </span>
          <span className="bg-red-500 text-white px-2 py-1 rounded">
            {String(timeLeft.minutes).padStart(2, '0')}m
          </span>
          <span className="bg-red-500 text-white px-2 py-1 rounded">
            {String(timeLeft.seconds).padStart(2, '0')}s
          </span>
        </>
      ) : (
        <span className="text-red-500 font-medium">Offer Expired</span>
      )}
    </div>
  );
};

export default AdPopup;